import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { btnPrimarySx } from '../../../utils/buttonSx';
import { usePageHeader } from '../../../context/PageHeaderContext';
import { scrollAreaSx } from '../../../utils/scrollSx';
import { RootState } from '../../../redux/store';
import axios from '../../../interceptors/axios';
import ActivationModal from './ActivationModal';
import ScreenDetailPage from './ScreenDetailPage';

interface DsScreen {
  id: number;
  device_id: string;
  display_name: string;
  last_seen_at: string | null;
  app_version: string | null;
  ip_address: string | null;
  config_version: number;
  created_at: string;
  default_content_type: string | null;
  default_playlist_id: number | null;
  schedule_id: number | null;
  files_on_device: number[] | null;
  storage_free_bytes: number | null;
  storage_total_bytes: number | null;
  debug_mode: boolean;
  kiosk_mode: boolean;
  disable_standby: boolean;
  autostart_on_boot: boolean;
  screen_orientation: number;
  content_rotation: number;
  cache_ttl_days: number;
  last_tally_at: string | null;
  last_seq: number;
}

interface Playlist { id: number; name: string; }
interface Schedule { id: number; name: string; }

const ONLINE_MS = 6 * 60 * 1000;
const isOnline = (last: string | null) => !!last && Date.now() - new Date(last).getTime() < ONLINE_MS;

const fmtRelative = (d: string | null, never: string) => {
  if (!d) return never;
  const s = Math.round((Date.now() - new Date(d).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

// ── Icons ──────────────────────────────────────────────────────────────────────

const IcoPlus = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const IcoScreen = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="20" height="14" rx="2" />
    <line x1="8" y1="21" x2="16" y2="21" />
    <line x1="12" y1="17" x2="12" y2="21" />
  </svg>
);

const IcoChevron = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const IcoPlaylist = ({ size = 13 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/>
    <circle cx="3" cy="6" r="1" fill="currentColor" stroke="none"/><circle cx="3" cy="12" r="1" fill="currentColor" stroke="none"/><circle cx="3" cy="18" r="1" fill="currentColor" stroke="none"/>
  </svg>
);

const IcoCalendar = ({ size = 12 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);

// ── Shared styles ──────────────────────────────────────────────────────────────

const glassSx = {
  background: 'var(--glass-bg)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  border: '1px solid var(--glass-border)',
  borderRadius: '14px',
};

const fmtBytes = (b: number | null) => {
  if (b == null) return '—';
  if (b >= 1073741824) return `${(b / 1073741824).toFixed(1)}G`;
  if (b >= 1048576) return `${Math.round(b / 1048576)}M`;
  return `${Math.round(b / 1024)}K`;
};

const BoolBadge: React.FC<{ active: boolean; label: string }> = ({ active, label }) => (
  <Box sx={{ display: 'inline-flex', px: '7px', py: '2px', borderRadius: '20px', fontSize: 10, fontWeight: 600,
    bgcolor: active ? 'rgba(52,211,153,.12)' : 'rgba(148,163,184,.08)',
    color: active ? '#34d399' : '#94a3b8',
    border: `1px solid ${active ? 'rgba(52,211,153,.25)' : 'rgba(148,163,184,.15)'}`,
  }}>{label}</Box>
);

const IcoColumns = () => (
  <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2"/><line x1="9" y1="3" x2="9" y2="21"/><line x1="15" y1="3" x2="15" y2="21"/>
  </svg>
);

const IcoSync = () => (
  <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <polyline points="1 4 1 10 7 10"/><polyline points="23 20 23 14 17 14"/>
    <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10M23 14l-4.64 4.36A9 9 0 0 1 3.51 15"/>
  </svg>
);

const IcoDownload = () => (
  <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);

// ── Column definitions ────────────────────────────────────────────────────────

interface ColDef { key: string; label: string; defaultVisible: boolean; }

const COLS: ColDef[] = [
  { key: 'device_id',   label: 'Device ID',    defaultVisible: true  },
  { key: 'name',        label: 'Name',          defaultVisible: true  },
  { key: 'status',      label: 'Status',        defaultVisible: true  },
  { key: 'last_seen',   label: 'Last Seen',     defaultVisible: true  },
  { key: 'content',     label: 'Content',       defaultVisible: true  },
  { key: 'ip',          label: 'IP Address',    defaultVisible: false },
  { key: 'version',     label: 'App Version',   defaultVisible: false },
  { key: 'debug',       label: 'Debug Mode',    defaultVisible: false },
  { key: 'kiosk',       label: 'Kiosk',         defaultVisible: false },
  { key: 'standby',     label: 'Standby Off',   defaultVisible: false },
  { key: 'autostart',   label: 'Autostart',     defaultVisible: false },
  { key: 'orientation', label: 'Orientation',   defaultVisible: false },
  { key: 'rotation',    label: 'Rotation',      defaultVisible: false },
  { key: 'cache_ttl',   label: 'Cache TTL',     defaultVisible: false },
  { key: 'config_ver',  label: 'Config Ver',    defaultVisible: false },
  { key: 'storage',     label: 'Storage',       defaultVisible: false },
  { key: 'files',       label: 'Files',         defaultVisible: false },
  { key: 'last_tally',  label: 'Last Tally',    defaultVisible: false },
];

const COL_STORAGE_KEY = 'pv_screen_cols_v1';
const DEFAULT_VISIBLE = new Set(COLS.filter(c => c.defaultVisible).map(c => c.key));

function loadColVisibility(): Set<string> {
  try {
    const raw = localStorage.getItem(COL_STORAGE_KEY);
    if (raw) return new Set(JSON.parse(raw) as string[]);
  } catch { /* ignore */ }
  return new Set(DEFAULT_VISIBLE);
}

// ── KPI Card ──────────────────────────────────────────────────────────────────

const KpiCard: React.FC<{ label: string; value: number | string; color: string; glow: string }> = ({ label, value, color, glow }) => (
  <Box sx={{
    ...glassSx, p: '18px 20px', position: 'relative', overflow: 'hidden',
    '&::after': { content: '""', position: 'absolute', top: '-20px', right: '-20px', width: 80, height: 80, borderRadius: '50%', background: glow, filter: 'blur(22px)', pointerEvents: 'none' },
  }}>
    <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--text-disabled)', mb: '4px' }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: 32, fontWeight: 700, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums', color }}>
      {value}
    </Typography>
  </Box>
);

// ── Status badge ───────────────────────────────────────────────────────────────

const Badge: React.FC<{ variant: 'online' | 'offline' }> = ({ variant }) => {
  const cfg = {
    online:  { label: '● Online',  bg: 'rgba(16,185,129,.1)', color: '#10b981', border: 'rgba(16,185,129,.25)' },
    offline: { label: '● Offline', bg: 'rgba(148,163,184,.1)', color: '#94a3b8', border: 'rgba(148,163,184,.2)' },
  }[variant];
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px', px: '8px', py: '2px', borderRadius: '20px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap', bgcolor: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` }}>
      {cfg.label}
    </Box>
  );
};

// ── Content badge ─────────────────────────────────────────────────────────────

const ContentCell: React.FC<{ screen: DsScreen; playlists: Playlist[]; schedules: Schedule[] }> = ({ screen, playlists, schedules }) => {
  const playlistName = playlists.find(p => p.id === screen.default_playlist_id)?.name;
  const scheduleName = schedules.find(s => s.id === screen.schedule_id)?.name;
  const type = screen.default_content_type;

  if (!type || type === 'off') return <Typography sx={{ fontSize: 12, color: 'var(--text-disabled)' }}>—</Typography>;
  return (
    <Box>
      {(type === 'playlist' && playlistName) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: 12, color: 'var(--text-primary)' }}>
          <Box sx={{ color: 'var(--accent)', flexShrink: 0, display: 'flex' }}><IcoPlaylist /></Box>
          <Typography sx={{ fontSize: 12, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180 }}>{playlistName}</Typography>
        </Box>
      )}
      {(type === 'media') && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: 12, color: 'var(--text-primary)' }}>
          <Box sx={{ color: 'var(--accent)', flexShrink: 0, display: 'flex' }}>
            <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <polygon points="5 3 19 12 5 21 5 3" fill="currentColor" stroke="none"/>
            </svg>
          </Box>
          <Typography sx={{ fontSize: 12, color: 'var(--text-primary)' }}>Single file</Typography>
        </Box>
      )}
      {scheduleName && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', mt: '3px' }}>
          <Box sx={{ color: 'var(--text-disabled)', flexShrink: 0, display: 'flex' }}><IcoCalendar /></Box>
          <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180 }}>{scheduleName}</Typography>
        </Box>
      )}
    </Box>
  );
};

// ── Bulk field row ────────────────────────────────────────────────────────────

const BulkFieldRow: React.FC<{
  label: string; enabled: boolean; onToggle: () => void; summary: string; children: React.ReactNode;
}> = ({ label, enabled, onToggle, summary, children }) => (
  <Box sx={{ borderRadius: '10px', border: '1px solid var(--glass-border)', overflow: 'hidden' }}>
    <Box
      onClick={onToggle}
      sx={{ display: 'flex', alignItems: 'center', gap: '10px', px: '14px', py: '10px', cursor: 'pointer', bgcolor: 'var(--glass-hover)', '&:hover': { bgcolor: 'var(--row-hover)' } }}
    >
      {/* checkbox */}
      <Box sx={{
        width: 17, height: 17, borderRadius: '4px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all .12s',
        bgcolor: enabled ? 'var(--accent)' : 'transparent',
        border: `1.5px solid ${enabled ? 'var(--accent)' : 'var(--glass-border-strong)'}`,
      }}>
        {enabled && <svg width={10} height={10} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
      </Box>
      <Typography sx={{ flex: 1, fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{label}</Typography>
      <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)' }}>{summary}</Typography>
    </Box>
    {enabled && (
      <Box sx={{ px: '14px', py: '10px', bgcolor: 'var(--glass-bg)' }}>
        {children}
      </Box>
    )}
  </Box>
);

// ── Main ──────────────────────────────────────────────────────────────────────

interface Company { id: number; name: string; display_name: string; }

const ScreensPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const auth = useSelector((s: RootState) => s.auth.value);
  const currentUser = useSelector((s: RootState) => s.auth.user);

  const [screens, setScreens]     = useState<DsScreen[]>([]);
  const [playlists, setPlaylists]  = useState<Playlist[]>([]);
  const [schedules, setSchedules]  = useState<Schedule[]>([]);
  const [loading, setLoading]      = useState(true);
  const [activateOpen, setActivateOpen] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const [filterChip, setFilterChip] = useState<'all' | 'online' | 'offline'>('all');
  const { setActionConfig } = usePageHeader();
  // below md the in-page toolbar is hidden, so the action goes to the topbar
  const isNarrow = useMediaQuery('(max-width:899px)');

  // ── Multi-select ──────────────────────────────────────────────────────────────
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [bulkOpen,  setBulkOpen]  = useState(false);
  const [bEnabled,  setBEnabled]  = useState<Set<string>>(new Set());
  const [bContentType,     setBContentType]     = useState<'playlist' | 'schedule' | 'clear'>('playlist');
  const [bPlaylistId,      setBPlaylistId]      = useState<number | null>(null);
  const [bSchedId,         setBSchedId]         = useState<number | null>(null);
  const [bOrientation,     setBOrientation]     = useState<number | null>(null);
  const [bRotation,        setBRotation]        = useState<number | null>(null);
  const [bAutostart,       setBAutostart]       = useState<boolean | null>(null);
  const [bStandby,         setBStandby]         = useState<boolean | null>(null);
  const [bCacheTtl,        setBCacheTtl]        = useState<number | null>(null);
  const [bCompanyName,     setBCompanyName]     = useState<string | null>(null);
  const [bulkApplying,     setBulkApplying]     = useState(false);
  const [companies,        setCompanies]        = useState<Company[]>([]);

  const [visibleCols, setVisibleCols] = useState<Set<string>>(loadColVisibility);
  const [colPickerOpen, setColPickerOpen] = useState(false);
  const colPickerRef = useRef<HTMLDivElement>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const f = searchParams.get('filter');
    if (!f) return;
    if (f === 'online' || f === 'offline') setFilterChip(f);
    setSearchParams({}, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const tickRef = useRef(0);

  const load = useCallback(async () => {
    try {
      const [{ data: sc }, { data: pl }, { data: sched }] = await Promise.all([
        axios.get<DsScreen[]>('/screens/devices'),
        axios.get<Playlist[]>('/playlists'),
        axios.get<Schedule[]>('/schedules'),
      ]);
      setScreens(sc);
      setPlaylists(Array.isArray(pl) ? pl : []);
      setSchedules(Array.isArray(sched) ? sched : []);
    } catch { /* interceptor handles auth */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (auth && currentUser?.isAdmin) {
      axios.get<Company[]>('/company/companies').then(r => setCompanies(r.data)).catch(() => {});
    }
  }, [auth, currentUser?.isAdmin]);

  useEffect(() => { if (auth) load(); }, [auth, load]);

  useEffect(() => {
    const timer = setInterval(() => {
      tickRef.current += 1;
      if (auth) load();
    }, 30_000);
    return () => clearInterval(timer);
  }, [auth, load]);

  const deleteScreen = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirmDeleteId !== id) { setConfirmDeleteId(id); return; }
    setDeletingId(id);
    try {
      await axios.delete(`/screens/devices/${id}`);
      setScreens(prev => prev.filter(s => s.id !== id));
    } catch { /* ignore */ }
    finally { setDeletingId(null); setConfirmDeleteId(null); }
  };

  // ── Selection helpers ─────────────────────────────────────────────────────────
  const toggleSelect = useCallback((id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelected(prev => {
      const ids = (filterChip === 'online'
        ? screens.filter(s =>  isOnline(s.last_seen_at))
        : filterChip === 'offline'
        ? screens.filter(s => !isOnline(s.last_seen_at))
        : screens).map(s => s.id);
      const allSel = ids.every(id => prev.has(id));
      const next = new Set(prev);
      if (allSel) ids.forEach(id => next.delete(id));
      else ids.forEach(id => next.add(id));
      return next;
    });
  }, [screens, filterChip]);

  const clearSelection = () => { setSelected(new Set()); };

  const toggleCol = (key: string) => {
    setVisibleCols(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      try { localStorage.setItem(COL_STORAGE_KEY, JSON.stringify(Array.from(next))); } catch { /* ignore */ }
      return next;
    });
  };

  const sendBulkCommand = async (command: string, payload?: Record<string, unknown>) => {
    for (const id of Array.from(selected)) {
      await axios.post(`/screens/devices/${id}/commands`, { command, payload }).catch(() => {});
    }
    setSelected(new Set());
  };

  const openBulkEdit = () => {
    setBEnabled(new Set());
    setBContentType('playlist');
    setBPlaylistId(null);
    setBSchedId(null);
    setBOrientation(null);
    setBRotation(null);
    setBAutostart(null);
    setBStandby(null);
    setBCacheTtl(null);
    setBCompanyName(null);
    setBulkOpen(true);
  };

  const toggleBField = (key: string) => {
    setBEnabled(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const bulkApply = async () => {
    setBulkApplying(true);
    try {
      const ids = Array.from(selected);

      // Company reassignment — single bulk call
      if (bEnabled.has('company') && bCompanyName) {
        await axios.post('/screens/devices/bulk-reassign', { screen_ids: ids, company_name: bCompanyName }).catch(() => {});
      }

      for (const id of ids) {
        const patch: Record<string, unknown> = {};
        if (bEnabled.has('defaultContent') && bPlaylistId !== null) {
          patch.default_content_type = 'playlist';
          patch.default_playlist_id = bPlaylistId;
        }
        if (bEnabled.has('scheduledContent') && bSchedId !== null) {
          patch.schedule_id = bSchedId;
        }
        if (bEnabled.has('orientation') && bOrientation !== null) patch.screen_orientation = bOrientation;
        if (bEnabled.has('rotation') && bRotation !== null) patch.content_rotation = bRotation;
        if (bEnabled.has('autostart') && bAutostart !== null) patch.autostart_on_boot = bAutostart;
        if (bEnabled.has('standby') && bStandby !== null) patch.disable_standby = bStandby;
        if (bEnabled.has('cacheTtl') && bCacheTtl !== null) patch.cache_ttl_days = bCacheTtl;
        if (Object.keys(patch).length) {
          await axios.put(`/screens/devices/${id}`, patch).catch(() => {});
        }
      }
      await load();
      setBulkOpen(false);
      setSelected(new Set());
    } finally {
      setBulkApplying(false);
    }
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (colPickerRef.current && !colPickerRef.current.contains(e.target as Node)) {
        setColPickerOpen(false);
      }
    };
    if (colPickerOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [colPickerOpen]);

  const renderCell = (col: string, screen: DsScreen) => {
    const online = isOnline(screen.last_seen_at);
    const ORIENTS = ['Landscape', 'Portrait', 'Rev Land.', 'Rev Port.'];
    switch (col) {
      case 'device_id':
        return (
          <Typography sx={{ fontSize: 12, fontFamily: '"JetBrains Mono", monospace', color: 'var(--text-secondary)', letterSpacing: '.03em' }}>
            <Box component="span" sx={{ color: 'var(--text-disabled)', fontSize: 10 }}>#</Box>{screen.device_id}
          </Typography>
        );
      case 'name':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Box sx={{ width: 30, height: 30, borderRadius: '8px', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: '1px solid var(--glass-border)',
              bgcolor: online ? 'rgba(2,132,199,.12)' : 'rgba(148,163,184,.08)',
              color: online ? 'var(--accent)' : '#94a3b8',
            }}>
              <IcoScreen size={14} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 500, color: screen.display_name ? 'var(--text-primary)' : 'var(--text-disabled)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220, fontStyle: screen.display_name ? 'normal' : 'italic' }}>
                {screen.display_name || 'Unnamed screen'}
              </Typography>
              <Box sx={{ display: 'flex', gap: '8px', mt: '2px', flexWrap: 'wrap' }}>
                {screen.app_version && <Typography sx={{ fontSize: 10, color: 'var(--text-disabled)', fontFamily: '"JetBrains Mono", monospace' }}>v{screen.app_version}</Typography>}
                {Array.isArray(screen.files_on_device) && <Typography sx={{ fontSize: 10, color: 'var(--text-disabled)' }}>{screen.files_on_device.length} files</Typography>}
                {screen.storage_free_bytes != null && screen.storage_free_bytes > 0 && (
                  <Typography sx={{ fontSize: 10, color: 'var(--text-disabled)' }}>{fmtBytes(screen.storage_free_bytes)} free</Typography>
                )}
              </Box>
            </Box>
          </Box>
        );
      case 'status':
        return <Badge variant={online ? 'online' : 'offline'} />;
      case 'last_seen':
        return (
          <Typography sx={{ fontSize: 12, color: screen.last_seen_at ? 'var(--text-secondary)' : 'var(--text-disabled)' }}>
            {fmtRelative(screen.last_seen_at, '—')}
          </Typography>
        );
      case 'content':
        return <ContentCell screen={screen} playlists={playlists} schedules={schedules} />;
      case 'ip':
        return <Typography sx={{ fontSize: 11, fontFamily: '"JetBrains Mono", monospace', color: screen.ip_address ? 'var(--text-secondary)' : 'var(--text-disabled)' }}>{screen.ip_address || '—'}</Typography>;
      case 'version':
        return <Typography sx={{ fontSize: 11, fontFamily: '"JetBrains Mono", monospace', color: screen.app_version ? 'var(--text-secondary)' : 'var(--text-disabled)' }}>{screen.app_version ? `v${screen.app_version}` : '—'}</Typography>;
      case 'debug':
        return <BoolBadge active={screen.debug_mode} label={screen.debug_mode ? 'On' : 'Off'} />;
      case 'kiosk':
        return <BoolBadge active={screen.kiosk_mode} label={screen.kiosk_mode ? 'On' : 'Off'} />;
      case 'standby':
        return <BoolBadge active={screen.disable_standby} label={screen.disable_standby ? 'On' : 'Off'} />;
      case 'autostart':
        return <BoolBadge active={screen.autostart_on_boot} label={screen.autostart_on_boot ? 'On' : 'Off'} />;
      case 'orientation':
        return <Typography sx={{ fontSize: 11, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{ORIENTS[screen.screen_orientation] ?? `${screen.screen_orientation}`}</Typography>;
      case 'rotation':
        return <Typography sx={{ fontSize: 11, color: 'var(--text-secondary)' }}>{`${[0, 90, 180, 270][screen.content_rotation] ?? screen.content_rotation}°`}</Typography>;
      case 'cache_ttl':
        return <Typography sx={{ fontSize: 11, color: screen.cache_ttl_days > 0 ? 'var(--text-secondary)' : 'var(--text-disabled)' }}>{screen.cache_ttl_days > 0 ? `${screen.cache_ttl_days}d` : '—'}</Typography>;
      case 'config_ver':
        return <Typography sx={{ fontSize: 11, fontFamily: '"JetBrains Mono", monospace', color: 'var(--text-secondary)' }}>{screen.config_version}</Typography>;
      case 'storage':
        return screen.storage_total_bytes
          ? <Typography sx={{ fontSize: 11, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{fmtBytes(screen.storage_free_bytes)} / {fmtBytes(screen.storage_total_bytes)}</Typography>
          : <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)' }}>—</Typography>;
      case 'files':
        return <Typography sx={{ fontSize: 11, color: Array.isArray(screen.files_on_device) ? 'var(--text-secondary)' : 'var(--text-disabled)' }}>{Array.isArray(screen.files_on_device) ? screen.files_on_device.length : '—'}</Typography>;
      case 'last_tally':
        return <Typography sx={{ fontSize: 12, color: screen.last_tally_at ? 'var(--text-secondary)' : 'var(--text-disabled)' }}>{fmtRelative(screen.last_tally_at, '—')}</Typography>;
      default:
        return null;
    }
  };

  // Mobile: the add button moves into the topbar
  useEffect(() => {
    if (!isNarrow) { setActionConfig(null); return; }
    setActionConfig({ label: t('screens.addScreen'), onClick: () => setActivateOpen(true) });
    return () => setActionConfig(null);
  }, [isNarrow, setActionConfig, t]);

  const onlineCount  = screens.filter(s => isOnline(s.last_seen_at)).length;
  const offlineCount = screens.length - onlineCount;
  const filteredScreens = filterChip === 'online'
    ? screens.filter(s =>  isOnline(s.last_seen_at))
    : filterChip === 'offline'
    ? screens.filter(s => !isOnline(s.last_seen_at))
    : screens;

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0, minHeight: 0 }}>

      {/* ── Toolbar — desktop only: on mobile the topbar hosts the action and the
             filter chips live above the cards ── */}
      <Box sx={{
        flexShrink: 0, px: '22px', height: 52,
        display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: '10px',
        borderBottom: '1px solid var(--divider)',
        background: 'var(--topbar-bg)', backdropFilter: 'blur(20px)',
      }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
          {t('screens.title')}
        </Typography>
        <Typography sx={{ fontSize: 13, color: 'var(--text-disabled)' }}>/</Typography>
        <Typography sx={{ fontSize: 13, color: 'var(--text-disabled)' }}>
          {screens.length} {screens.length === 1 ? 'screen' : 'screens'}
        </Typography>

        {/* ── Status filter chips ── */}
        <Box sx={{ display: 'flex', gap: '6px', ml: '6px' }}>
          {([
            ['all',     'All',                         'var(--accent)',  'rgba(99,102,241,.12)'],
            ['online',  `Online · ${onlineCount}`,     '#34d399',        'rgba(52,211,153,.12)'],
            ['offline', `Offline · ${offlineCount}`,   '#f87171',        'rgba(248,113,113,.12)'],
          ] as [typeof filterChip, string, string, string][]).map(([val, label, color, bg]) => (
            <Box
              key={val}
              component="button"
              onClick={() => setFilterChip(val)}
              sx={{
                px: '10px', py: '4px', borderRadius: '20px', fontFamily: 'inherit',
                fontSize: 11, fontWeight: 600, cursor: 'pointer', transition: 'all .12s',
                border: `1px solid ${filterChip === val ? color : 'var(--glass-border)'}`,
                bgcolor: filterChip === val ? bg : 'transparent',
                color: filterChip === val ? color : 'var(--text-disabled)',
                '&:hover': { borderColor: color, color },
              }}
            >
              {label}
            </Box>
          ))}
        </Box>

        <Box sx={{ flex: 1 }} />
        <Box
          component="button"
          onClick={() => setActivateOpen(true)}
          sx={btnPrimarySx}
        >
          <IcoPlus /> {t('screens.addScreen')}
        </Box>
      </Box>

      {/* ── Scroll area ── */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: '16px', display: 'flex', flexDirection: 'column', gap: '12px', ...scrollAreaSx }}>

        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', pt: '80px', gap: '10px' }}>
            <Box sx={{ width: 20, height: 20, borderRadius: '50%', border: '2px solid var(--glass-border)', borderTopColor: 'var(--accent)', animation: 'spin .7s linear infinite', '@keyframes spin': { to: { transform: 'rotate(360deg)' } } }} />
            <Typography sx={{ fontSize: 13, color: 'var(--text-disabled)' }}>Loading…</Typography>
          </Box>
        ) : (
          <>
            {/* ── Screens ── */}
            {screens.length === 0 ? (
              <Box sx={{ ...glassSx, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: '80px', gap: '14px', opacity: .5 }}>
                <IcoScreen size={44} />
                <Typography sx={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>{t('screens.noScreens')}</Typography>
              </Box>
            ) : (
              <>

                {/* ── Mobile view ── */}
                <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: '10px' }}>

                  {/* Filter chips */}
                  <Box sx={{ display: 'flex', gap: '8px' }}>
                    {([
                      ['all',     `All (${screens.length})`],
                      ['online',  `Online (${onlineCount})`],
                      ['offline', `Offline (${offlineCount})`],
                    ] as ['all' | 'online' | 'offline', string][]).map(([val, label]) => (
                      <Box key={val} onClick={() => setFilterChip(val)}
                        sx={{ px: '12px', py: '5px', borderRadius: '20px', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                          border: `1px solid ${filterChip === val ? (val === 'online' ? '#34d399' : val === 'offline' ? '#f87171' : 'var(--accent)') : 'var(--glass-border)'}`,
                          bgcolor: filterChip === val
                            ? (val === 'online' ? 'rgba(52,211,153,.12)' : val === 'offline' ? 'rgba(248,113,113,.12)' : 'rgba(99,102,241,.15)')
                            : 'transparent',
                          color: filterChip === val
                            ? (val === 'online' ? '#34d399' : val === 'offline' ? '#f87171' : 'var(--accent)')
                            : 'var(--text-secondary)',
                          transition: 'all .12s',
                        }}>
                        {label}
                      </Box>
                    ))}
                  </Box>

                  {/* Device cards */}
                  {filteredScreens.map(screen => {
                    const online = isOnline(screen.last_seen_at);
                    const isSelected = selected.has(screen.id);
                    return (
                      <Box key={screen.id} onClick={() => navigate(`/screens/${screen.id}`)}
                        sx={{ ...glassSx, borderRadius: '12px', p: '12px 14px', display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer',
                          '&:hover': { bgcolor: 'var(--glass-hover)' }, transition: 'background .12s, border-color .12s' }}>
                        {/* Checkbox */}
                        <Box onClick={e => { e.stopPropagation(); toggleSelect(screen.id, e as any); }}
                          className={`cb-box${isSelected ? ' on' : ''}`}
                          style={{ flexShrink: 0 }}>
                          {isSelected && <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" style={{ width: 10, height: 10 }}><polyline points="20 6 9 17 4 12"/></svg>}
                        </Box>
                        <Box sx={{ width: 38, height: 38, borderRadius: '9px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                          border: '1px solid var(--glass-border)',
                          bgcolor: online ? 'rgba(2,132,199,.12)' : 'rgba(148,163,184,.08)',
                          color: online ? 'var(--accent)' : '#94a3b8',
                        }}>
                          <IcoScreen size={17} />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {screen.display_name || 'Unnamed screen'}
                          </Typography>
                          <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)', fontFamily: '"JetBrains Mono", monospace' }}>
                            #{screen.device_id} · {fmtRelative(screen.last_seen_at, '—')}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', px: '8px', py: '2px', borderRadius: '20px', fontSize: 11, fontWeight: 600, flexShrink: 0,
                          bgcolor: online ? 'rgba(16,185,129,.1)' : 'rgba(148,163,184,.1)',
                          color: online ? '#10b981' : '#94a3b8',
                          border: `1px solid ${online ? 'rgba(16,185,129,.25)' : 'rgba(148,163,184,.2)'}`,
                        }}>
                          {online ? '● Online' : '● Offline'}
                        </Box>
                      </Box>
                    );
                  })}
                  {filteredScreens.length === 0 && (
                    <Box sx={{ py: '40px', textAlign: 'center', color: 'var(--text-disabled)', fontSize: 13 }}>
                      No {filterChip} screens
                    </Box>
                  )}
                </Box>

                {/* ── Desktop table ── */}
              <Box sx={{ ...glassSx, overflow: 'hidden', display: { xs: 'none', md: 'block' } }}>

                {/* Column picker toolbar */}
                <Box sx={{ px: '18px', py: '8px', borderBottom: '1px solid var(--glass-border)', display: 'flex', alignItems: 'center', background: 'var(--glass-hover)' }}>
                  <Typography sx={{ flex: 1, fontSize: 11, color: 'var(--text-disabled)' }}>
                    {filteredScreens.length} {filteredScreens.length === 1 ? 'device' : 'devices'}
                  </Typography>
                  <Box ref={colPickerRef} sx={{ position: 'relative' }}>
                    <Box
                      component="button"
                      onClick={() => setColPickerOpen(v => !v)}
                      sx={{ display: 'inline-flex', alignItems: 'center', gap: '5px', px: '10px', py: '5px', borderRadius: '8px', fontFamily: 'inherit',
                        bgcolor: colPickerOpen ? 'var(--glass-hover)' : 'transparent',
                        border: `1px solid ${colPickerOpen ? 'var(--glass-border-strong)' : 'var(--glass-border)'}`,
                        color: colPickerOpen ? 'var(--text-primary)' : 'var(--text-secondary)',
                        fontSize: 11, fontWeight: 600, cursor: 'pointer',
                        '&:hover': { bgcolor: 'var(--glass-hover)', color: 'var(--text-primary)' },
                      }}
                    >
                      <IcoColumns />
                      Columns
                    </Box>
                    {colPickerOpen && (
                      <Box sx={{ position: 'absolute', right: 0, top: 'calc(100% + 4px)', zIndex: 300,
                        background: 'var(--glass-bg)', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)',
                        border: '1px solid var(--glass-border)', borderRadius: '12px', p: '6px',
                        minWidth: 170, boxShadow: '0 8px 24px rgba(0,0,0,.3)',
                      }}>
                        {COLS.map(col => (
                          <Box key={col.key} onClick={() => toggleCol(col.key)}
                            sx={{ display: 'flex', alignItems: 'center', gap: '8px', px: '10px', py: '6px', borderRadius: '7px', cursor: 'pointer',
                              '&:hover': { bgcolor: 'var(--glass-hover)' }, transition: 'background .1s',
                            }}
                          >
                            <Box sx={{ width: 14, height: 14, borderRadius: '3px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                              bgcolor: visibleCols.has(col.key) ? 'var(--accent)' : 'transparent',
                              border: `1.5px solid ${visibleCols.has(col.key) ? 'var(--accent)' : 'var(--glass-border-strong)'}`,
                            }}>
                              {visibleCols.has(col.key) && (
                                <svg width={9} height={9} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="20 6 9 17 4 12"/>
                                </svg>
                              )}
                            </Box>
                            <Typography sx={{ fontSize: 12, color: 'var(--text-primary)' }}>{col.label}</Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* Table */}
                <Box sx={{ overflowX: 'auto' }}>
                  <style>{`
                    .screens-table tbody tr:hover td { background: var(--row-hover); }
                    .screens-table tbody tr.sel td { background: var(--row-selected) !important; }
                    .screens-table tbody tr td { transition: background .1s; }
                    .screens-table .cb-cell { width: 40px; padding: 0 8px 0 18px !important; }
                    .screens-table .cb-box { width: 16px; height: 16px; border-radius: 4px; border: 1.5px solid var(--glass-border-strong); display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all .12s; flex-shrink: 0; }
                    .screens-table .cb-box.on { background: var(--accent); border-color: var(--accent); }
                    .screens-table .cb-box.partial { background: var(--accent); border-color: var(--accent); }
                    .screens-table .cb-box svg { width: 10px; height: 10px; color: #fff; }
                  `}</style>
                  <table className="screens-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th className="cb-cell" style={{ borderBottom: '1px solid var(--glass-border)', background: 'var(--glass-hover)', width: 40 }}>
                          <div
                            className={`cb-box${filteredScreens.length > 0 && filteredScreens.every(s => selected.has(s.id)) ? ' on' : filteredScreens.some(s => selected.has(s.id)) ? ' partial' : ''}`}
                            onClick={toggleAll}
                          >
                            {selected.size === screens.length && screens.length > 0 ? (
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                            ) : filteredScreens.some(s => selected.has(s.id)) ? (
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3}><line x1="5" y1="12" x2="19" y2="12"/></svg>
                            ) : null}
                          </div>
                        </th>
                        {COLS.filter(c => visibleCols.has(c.key)).map(col => (
                          <th key={col.key} style={{
                            padding: '8px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700,
                            textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--text-disabled)',
                            borderBottom: '1px solid var(--glass-border)', background: 'var(--glass-hover)', whiteSpace: 'nowrap',
                          }}>
                            {col.label}
                          </th>
                        ))}
                        <th style={{ borderBottom: '1px solid var(--glass-border)', background: 'var(--glass-hover)', width: 36, padding: '8px 18px 8px 14px' }} />
                      </tr>
                    </thead>
                    <tbody>
                      {filteredScreens.map(screen => {
                        const isSel = selected.has(screen.id);
                        return (
                          <tr
                            key={screen.id}
                            className={isSel ? 'sel' : undefined}
                            onClick={() => navigate(`/screens/${screen.id}`)}
                            style={{ cursor: 'pointer' }}
                          >
                            <td className="cb-cell" style={{ borderBottom: '1px solid var(--divider)' }} onClick={e => toggleSelect(screen.id, e)}>
                              <div className={`cb-box${isSel ? ' on' : ''}`}>
                                {isSel && <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
                              </div>
                            </td>
                            {COLS.filter(c => visibleCols.has(c.key)).map(col => (
                              <td key={col.key} style={{ padding: '11px 14px', borderBottom: '1px solid var(--divider)', whiteSpace: 'nowrap' }}>
                                {renderCell(col.key, screen)}
                              </td>
                            ))}
                            <td style={{ padding: '11px 18px 11px 14px', borderBottom: '1px solid var(--divider)', textAlign: 'right' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '6px' }}>
                                {confirmDeleteId === screen.id ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }} onClick={e => e.stopPropagation()}>
                                    <Typography sx={{ fontSize: 11, color: '#f87171', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                      {t('screens.deleteDeviceConfirm')}
                                    </Typography>
                                    <Box component="button" onClick={e => deleteScreen(screen.id, e)} disabled={deletingId === screen.id}
                                      sx={{ px: '8px', py: '3px', borderRadius: '6px', fontSize: 11, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer', bgcolor: 'rgba(248,113,113,.15)', border: '1px solid rgba(248,113,113,.3)', color: '#f87171', '&:hover': { bgcolor: 'rgba(248,113,113,.25)' } }}>
                                      {deletingId === screen.id ? '…' : 'Yes'}
                                    </Box>
                                    <Box component="button" onClick={e => { e.stopPropagation(); setConfirmDeleteId(null); }}
                                      sx={{ px: '8px', py: '3px', borderRadius: '6px', fontSize: 11, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', bgcolor: 'var(--glass-bg)', border: '1px solid var(--glass-border)', color: 'var(--text-secondary)', '&:hover': { color: 'var(--text-primary)' } }}>
                                      No
                                    </Box>
                                  </Box>
                                ) : (
                                  <Box component="button" onClick={e => deleteScreen(screen.id, e)}
                                    title={t('screens.deleteDevice')}
                                    sx={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '7px', cursor: 'pointer', bgcolor: 'transparent', border: '1px solid transparent', color: 'var(--text-disabled)', transition: 'all .15s', '&:hover': { bgcolor: 'rgba(248,113,113,.1)', borderColor: 'rgba(248,113,113,.25)', color: '#f87171' } }}>
                                    <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                                      <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                                    </svg>
                                  </Box>
                                )}
                                <Box sx={{ color: 'var(--text-disabled)' }}>
                                  <IcoChevron />
                                </Box>
                              </Box>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </Box>

                {/* Card footer */}
                <Box sx={{ px: '18px', py: '9px', borderTop: '1px solid var(--glass-border)', background: 'var(--glass-hover)' }}>
                  <Typography sx={{ fontSize: 12, color: 'var(--text-disabled)' }}>
                    {screens.length} screens · {onlineCount} online · {offlineCount} offline
                  </Typography>
                </Box>
              </Box>
              </>
            )}
          </>
        )}
      </Box>

      <ActivationModal
        open={activateOpen}
        onClose={() => setActivateOpen(false)}
        onSuccess={async (id) => { await load(); navigate(`/screens/${id}`); }}
      />

      {/* ── Floating pill toolbar ── */}
      {selected.size > 0 && (
        <Box sx={{
          position: 'fixed', bottom: '20px', left: '50%', transform: 'translateX(-50%)',
          display: 'flex', alignItems: 'center', gap: '4px',
          background: 'var(--glass-bg)', backdropFilter: 'blur(24px)',
          border: '1px solid var(--glass-border-strong)',
          borderRadius: '40px', px: '6px', py: '6px',
          boxShadow: '0 8px 32px rgba(0,0,0,.35)',
          zIndex: 1200, userSelect: 'none',
        }}>
          {/* Count badge */}
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: '6px',
            px: '12px', py: '4px', borderRadius: '20px',
            bgcolor: 'rgba(2,132,199,.12)', border: '1px solid rgba(2,132,199,.2)',
          }}>
            <IcoScreen size={13} />
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)', whiteSpace: 'nowrap' }}>
              {t('screens.bulk.screenCount', { count: selected.size })}
            </Typography>
          </Box>

          {/* Divider */}
          <Box sx={{ width: '1px', height: '22px', bgcolor: 'var(--divider)', mx: '2px' }} />

          {/* Reload Config */}
          <Box
            component="button"
            onClick={() => sendBulkCommand('reload_config')}
            title={t('screens.bulk.reloadConfigTitle')}
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: '5px',
              px: '12px', py: '5px', borderRadius: '20px',
              bgcolor: 'transparent', border: '1px solid var(--glass-border)', color: 'var(--text-secondary)',
              fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
              '&:hover': { bgcolor: 'var(--glass-hover)', color: 'var(--text-primary)', borderColor: 'var(--glass-border-strong)' },
            }}
          >
            <IcoSync />
            {t('screens.bulk.reloadConfig')}
          </Box>

          {/* Sync Files */}
          <Box
            component="button"
            onClick={() => sendBulkCommand('sync_files')}
            title={t('screens.bulk.syncFilesTitle')}
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: '5px',
              px: '12px', py: '5px', borderRadius: '20px',
              bgcolor: 'transparent', border: '1px solid var(--glass-border)', color: 'var(--text-secondary)',
              fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
              '&:hover': { bgcolor: 'var(--glass-hover)', color: 'var(--text-primary)', borderColor: 'var(--glass-border-strong)' },
            }}
          >
            <IcoDownload />
            {t('screens.bulk.syncFiles')}
          </Box>

          {/* Divider */}
          <Box sx={{ width: '1px', height: '22px', bgcolor: 'var(--divider)', mx: '2px' }} />

          {/* Bulk Edit */}
          <Box
            component="button"
            onClick={openBulkEdit}
            sx={{ ...btnPrimarySx, borderRadius: '20px' }}
          >
            <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
            {t('screens.bulk.bulkEdit')}
          </Box>

          {/* Close */}
          <Box
            component="button"
            onClick={clearSelection}
            title={t('screens.bulk.clearSelection')}
            sx={{
              width: 30, height: 30, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: 'transparent', border: '1px solid transparent', color: 'var(--text-disabled)',
              cursor: 'pointer', fontFamily: 'inherit',
              '&:hover': { bgcolor: 'var(--glass-hover)', color: 'var(--text-primary)' },
            }}
          >
            <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </Box>
        </Box>
      )}

      {/* ── Bulk Edit Modal ── */}
      {bulkOpen && (
        <Box
          onClick={() => setBulkOpen(false)}
          sx={{ position: 'fixed', inset: 0, bgcolor: 'rgba(0,0,0,.6)', backdropFilter: 'blur(6px)', zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <Box
            onClick={e => e.stopPropagation()}
            sx={{
              background: 'var(--glass-bg)', backdropFilter: 'blur(24px)',
              border: '1px solid var(--glass-border)', borderRadius: '16px',
              width: 440, maxWidth: '95vw', display: 'flex', flexDirection: 'column',
              boxShadow: '0 20px 60px rgba(0,0,0,.4)',
            }}
          >
            {/* Header */}
            <Box sx={{ px: '20px', py: '14px', borderBottom: '1px solid var(--glass-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{t('screens.bulk.bulkEdit')}</Typography>
              <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)', fontFamily: '"JetBrains Mono", monospace' }}>
                {t('screens.bulk.screensSelected', { count: selected.size })}
              </Typography>
            </Box>

            {/* Body */}
            <Box sx={{ px: '20px', py: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {/* Default Content */}
              <BulkFieldRow
                label={t('screens.bulk.defaultContent')}
                enabled={bEnabled.has('defaultContent')}
                onToggle={() => toggleBField('defaultContent')}
                summary={bEnabled.has('defaultContent') && bPlaylistId !== null ? (playlists.find(p => p.id === bPlaylistId)?.name ?? '—') : '—'}
              >
                <Box
                  component="select"
                  value={bPlaylistId ?? ''}
                  onChange={e => setBPlaylistId(e.target.value ? Number(e.target.value) : null)}
                  sx={{
                    width: '100%', px: '10px', py: '7px', borderRadius: '8px',
                    border: '1px solid var(--input-border)', bgcolor: 'var(--input-bg)',
                    color: 'var(--text-primary)', fontSize: 13, fontFamily: 'inherit', outline: 'none',
                    '&:focus': { borderColor: 'var(--accent)' },
                  }}
                >
                  <option value="">{t('screens.bulk.selectPlaylist')}</option>
                  {playlists.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </Box>
              </BulkFieldRow>

              {/* Scheduled Content */}
              <BulkFieldRow
                label={t('screens.bulk.scheduledContent')}
                enabled={bEnabled.has('scheduledContent')}
                onToggle={() => toggleBField('scheduledContent')}
                summary={bEnabled.has('scheduledContent') && bSchedId !== null ? (schedules.find(s => s.id === bSchedId)?.name ?? '—') : '—'}
              >
                <Box
                  component="select"
                  value={bSchedId ?? ''}
                  onChange={e => setBSchedId(e.target.value ? Number(e.target.value) : null)}
                  sx={{
                    width: '100%', px: '10px', py: '7px', borderRadius: '8px',
                    border: '1px solid var(--input-border)', bgcolor: 'var(--input-bg)',
                    color: 'var(--text-primary)', fontSize: 13, fontFamily: 'inherit', outline: 'none',
                    '&:focus': { borderColor: 'var(--accent)' },
                  }}
                >
                  <option value="">{t('screens.bulk.selectSchedule')}</option>
                  {schedules.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </Box>
              </BulkFieldRow>

              {/* Screen Orientation */}
              <BulkFieldRow
                label={t('screens.bulk.orientation')}
                enabled={bEnabled.has('orientation')}
                onToggle={() => toggleBField('orientation')}
                summary={bEnabled.has('orientation') && bOrientation !== null ? [t('screens.bulk.landscape'),t('screens.bulk.portrait'),t('screens.bulk.revLandscape'),t('screens.bulk.revPortrait')][bOrientation] ?? '—' : '—'}
              >
                <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {[t('screens.bulk.landscape'),t('screens.bulk.portrait'),t('screens.bulk.revLandscape'),t('screens.bulk.revPortrait')].map((label, idx) => (
                    <Box key={idx} component="button" onClick={() => setBOrientation(idx)}
                      sx={{ px: '12px', py: '4px', borderRadius: '20px', fontFamily: 'inherit', fontSize: 12, fontWeight: 600, cursor: 'pointer', transition: 'all .12s',
                        bgcolor: bOrientation === idx ? 'rgba(2,132,199,.14)' : 'transparent',
                        border: `1px solid ${bOrientation === idx ? 'rgba(2,132,199,.3)' : 'var(--glass-border)'}`,
                        color: bOrientation === idx ? 'var(--accent)' : 'var(--text-secondary)',
                      }}>{label}</Box>
                  ))}
                </Box>
              </BulkFieldRow>

              {/* Content Rotation */}
              <BulkFieldRow
                label={t('screens.bulk.contentRotation')}
                enabled={bEnabled.has('rotation')}
                onToggle={() => toggleBField('rotation')}
                summary={bEnabled.has('rotation') && bRotation !== null ? `${[0,90,180,270][bRotation]}°` : '—'}
              >
                <Box sx={{ display: 'flex', gap: '6px' }}>
                  {['0°','90°','180°','270°'].map((label, idx) => (
                    <Box key={idx} component="button" onClick={() => setBRotation(idx)}
                      sx={{ px: '12px', py: '4px', borderRadius: '20px', fontFamily: 'inherit', fontSize: 12, fontWeight: 600, cursor: 'pointer', transition: 'all .12s',
                        bgcolor: bRotation === idx ? 'rgba(2,132,199,.14)' : 'transparent',
                        border: `1px solid ${bRotation === idx ? 'rgba(2,132,199,.3)' : 'var(--glass-border)'}`,
                        color: bRotation === idx ? 'var(--accent)' : 'var(--text-secondary)',
                      }}>{label}</Box>
                  ))}
                </Box>
              </BulkFieldRow>

              {/* Autostart on Boot */}
              <BulkFieldRow
                label={t('screens.bulk.autostart')}
                enabled={bEnabled.has('autostart')}
                onToggle={() => toggleBField('autostart')}
                summary={bEnabled.has('autostart') && bAutostart !== null ? (bAutostart ? t('screens.bulk.enabled') : t('screens.bulk.disabled')) : '—'}
              >
                <Box sx={{ display: 'flex', gap: '6px' }}>
                  {([true, false] as const).map((val) => (
                    <Box key={String(val)} component="button" onClick={() => setBAutostart(val)}
                      sx={{ px: '12px', py: '4px', borderRadius: '20px', fontFamily: 'inherit', fontSize: 12, fontWeight: 600, cursor: 'pointer', transition: 'all .12s',
                        bgcolor: bAutostart === val ? 'rgba(2,132,199,.14)' : 'transparent',
                        border: `1px solid ${bAutostart === val ? 'rgba(2,132,199,.3)' : 'var(--glass-border)'}`,
                        color: bAutostart === val ? 'var(--accent)' : 'var(--text-secondary)',
                      }}>{val ? t('screens.bulk.enabled') : t('screens.bulk.disabled')}</Box>
                  ))}
                </Box>
              </BulkFieldRow>

              {/* Disable Standby */}
              <BulkFieldRow
                label={t('screens.bulk.standby')}
                enabled={bEnabled.has('standby')}
                onToggle={() => toggleBField('standby')}
                summary={bEnabled.has('standby') && bStandby !== null ? (bStandby ? t('screens.bulk.enabled') : t('screens.bulk.disabled')) : '—'}
              >
                <Box sx={{ display: 'flex', gap: '6px' }}>
                  {([true, false] as const).map((val) => (
                    <Box key={String(val)} component="button" onClick={() => setBStandby(val)}
                      sx={{ px: '12px', py: '4px', borderRadius: '20px', fontFamily: 'inherit', fontSize: 12, fontWeight: 600, cursor: 'pointer', transition: 'all .12s',
                        bgcolor: bStandby === val ? 'rgba(2,132,199,.14)' : 'transparent',
                        border: `1px solid ${bStandby === val ? 'rgba(2,132,199,.3)' : 'var(--glass-border)'}`,
                        color: bStandby === val ? 'var(--accent)' : 'var(--text-secondary)',
                      }}>{val ? t('screens.bulk.enabled') : t('screens.bulk.disabled')}</Box>
                  ))}
                </Box>
              </BulkFieldRow>

              {/* Cache TTL */}
              <BulkFieldRow
                label={t('screens.cacheTtlDays')}
                enabled={bEnabled.has('cacheTtl')}
                onToggle={() => toggleBField('cacheTtl')}
                summary={bEnabled.has('cacheTtl') && bCacheTtl !== null ? `${bCacheTtl}d` : '—'}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Box
                    component="input"
                    type="number"
                    min={0}
                    max={365}
                    value={bCacheTtl ?? ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBCacheTtl(Math.max(0, +e.target.value))}
                    sx={{ width: 64, background: 'var(--input-bg)', border: '1px solid var(--input-border)', borderRadius: '8px', px: '8px', py: '5px', fontSize: 13, color: 'var(--text-primary)', outline: 'none', textAlign: 'center', fontFamily: 'inherit', '&:focus': { borderColor: 'var(--accent)' } }}
                  />
                  <Typography sx={{ fontSize: 12, color: 'var(--text-disabled)' }}>
                    {bCacheTtl === 0 ? t('screens.offlineTimeoutUnlimited') : t('screens.cacheTtlDaysUnit')}
                  </Typography>
                </Box>
              </BulkFieldRow>

              {/* Company — admin only */}
              {currentUser?.isAdmin && companies.length > 0 && (
                <BulkFieldRow
                  label={t('screens.bulk.company')}
                  enabled={bEnabled.has('company')}
                  onToggle={() => toggleBField('company')}
                  summary={bEnabled.has('company') && bCompanyName ? (companies.find(c => c.name === bCompanyName)?.display_name ?? bCompanyName) : '—'}
                >
                  <Box
                    component="select"
                    value={bCompanyName ?? ''}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setBCompanyName(e.target.value || null)}
                    sx={{
                      width: '100%', px: '10px', py: '7px', borderRadius: '8px',
                      border: '1px solid var(--input-border)', bgcolor: 'var(--input-bg)',
                      color: 'var(--text-primary)', fontSize: 13, fontFamily: 'inherit', outline: 'none',
                      '&:focus': { borderColor: 'var(--accent)' },
                    }}
                  >
                    <option value="">{t('screens.bulk.selectCompany')}</option>
                    {companies.map(c => <option key={c.name} value={c.name}>{c.display_name}</option>)}
                  </Box>
                </BulkFieldRow>
              )}
            </Box>

            {/* Footer */}
            <Box sx={{ px: '20px', py: '14px', borderTop: '1px solid var(--glass-border)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Typography sx={{ flex: 1, fontSize: 11, color: 'var(--text-disabled)' }}>
                {bEnabled.size > 0 ? t('screens.bulk.fieldsWillChange', { count: bEnabled.size }) : t('screens.bulk.selectFields')}
              </Typography>
              <Box component="button" onClick={() => setBulkOpen(false)}
                sx={{ px: '16px', py: '7px', borderRadius: '8px', fontFamily: 'inherit', fontSize: 12, fontWeight: 600, cursor: 'pointer', bgcolor: 'var(--glass-hover)', border: '1px solid var(--glass-border)', color: 'var(--text-secondary)', '&:hover': { color: 'var(--text-primary)' } }}>
                {t('screens.bulk.cancel')}
              </Box>
              <Box component="button"
                onClick={bulkApply}
                disabled={bEnabled.size === 0 || bulkApplying}
                sx={{ px: '16px', py: '7px', borderRadius: '8px', fontFamily: 'inherit', fontSize: 12, fontWeight: 700, cursor: 'pointer', border: 'none',
                  background: 'linear-gradient(135deg, var(--accent), var(--accent-light))', color: '#fff',
                  boxShadow: '0 2px 8px var(--accent-glow)',
                  opacity: bEnabled.size === 0 || bulkApplying ? 0.4 : 1,
                  '&:disabled': { cursor: 'default' },
                }}>
                {bulkApplying ? t('screens.bulk.applying') : t('screens.bulk.apply', { count: selected.size })}
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ScreensPage;
